// ── Config ────────────────────────────────────────────────────────────────────
import { BACKEND } from '../utils/config'
import { buildAuthHeader } from '../utils/auth'

// Timeouts adaptés à la charge réseau
const TIMEOUT_DEFAULT_MS = 30_000   // 30s  — requêtes standard
const TIMEOUT_SAVE_MS    = 120_000  // 120s — saveEDL (12 photos × 2 uploads)
const TIMEOUT_AUTH_MS    = 15_000   // 15s  — auth uniquement

// ✅ Fetch avec timeout via AbortController
async function fetchWithTimeout(url, options = {}, timeoutMs = TIMEOUT_DEFAULT_MS) {
  const controller = new AbortController()
  const timer      = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(url, { ...options, signal: controller.signal })
  } catch (err) {
    // Distingue timeout vs erreur réseau pour un message UI utile
    if (err.name === 'AbortError') {
      throw new Error(`Délai dépassé (${timeoutMs / 1000}s) — vérifiez votre connexion`)
    }
    throw new Error(`Erreur réseau : ${err.message}`)
  } finally {
    clearTimeout(timer)
  }
}

// ✅ Appel API centralisé avec timeout configurable
async function apiCall(path, options = {}, credentials = null, timeoutMs = TIMEOUT_DEFAULT_MS) {
  const headers = { 'Content-Type': 'application/json', ...options.headers }
  if (credentials) headers['X-MRC-Auth'] = buildAuthHeader(credentials)

  const res = await fetchWithTimeout(`${BACKEND}${path}`, { ...options, headers }, timeoutMs)

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

// ── Service API ───────────────────────────────────────────────────────────────
export const apiService = {

  // ── Auth ────────────────────────────────────────────────────────────────────
  async login(username, password, db, societe) {
    return apiCall(
      '/api/auth',
      { method: 'POST', body: JSON.stringify({ username, password, db, societe }) },
      null,
      TIMEOUT_AUTH_MS
    )
  },

  // ── Contrats ────────────────────────────────────────────────────────────────
  async fetchContracts(credentials, codeAgence) {
    // ✅ encodeURIComponent pour sécuriser la valeur dans l'URL
    const params = codeAgence ? `?codeAgence=${encodeURIComponent(codeAgence)}` : ''
    return apiCall(`/api/contracts${params}`, {}, credentials)
  },

  async fetchContractByPlate(plate, credentials) {
    return apiCall(
      `/api/contracts/plate/${encodeURIComponent(plate)}`,
      {},
      credentials
    )
  },

  async fetchContractDetails(id, typeDocument = 'CL', credentials) {
    return apiCall(
      `/api/contracts/${encodeURIComponent(id)}/details?typeDocument=${encodeURIComponent(typeDocument)}`,
      {},
      credentials
    )
  },

  // ── Upload PDF standalone dans MRC ──────────────────────────────────────────
  async uploadDocument(contractId, pdfBase64, filename, inspectionType, credentials) {
    return apiCall(
      `/api/contracts/${contractId}/document`,
      { method: 'POST', body: JSON.stringify({ pdfBase64, filename, inspectionType }) },
      credentials,
      TIMEOUT_SAVE_MS // PDF peut être lourd
    )
  },

  // ── Sauvegarde EDL complète ─────────────────────────────────────────────────
  // Une seule requête qui déclenche côté backend :
  //   1. photos annotées  → Supabase Storage
  //   2. photos originales → MRC InsertDocument (parallèle + retry)
  //   3. PDF              → MRC InsertDocument
  //   4. métadonnées      → table edl Supabase (upsert)
  async saveEDL(contractId, type, payload, credentials) {
    // ✅ Guard : payload peut être undefined si appelé sans argument
    const safePayload = payload && typeof payload === 'object' ? payload : {}

    return apiCall(
      '/api/edl/save',
      {
        method: 'POST',
        body: JSON.stringify({
          contractId,
          type,
          mileage:     safePayload.mileage     ?? null,
          fuel:        safePayload.fuel        ?? null,
          damages:     safePayload.damages     || [],
          photos:      safePayload.photos      || {},  // { zone: { dataUrl, originalDataUrl } }
          agentName:   safePayload.agentName   || '',
          completedAt: safePayload.completedAt || new Date().toISOString(),
          pdfBase64:   safePayload.pdfBase64   || null,
          pdfFilename: safePayload.pdfFilename || `edl-${type}-${contractId}.pdf`,
        }),
      },
      credentials,
      TIMEOUT_SAVE_MS // ✅ 120s — 12 photos × 2 destinations en parallèle
    )
  },

  // ── Récupérer photos départ (URLs Supabase + idDocs MRC) ────────────────────
  async getDeparturePhotos(contractId, credentials) {
    return apiCall(
      `/api/edl/${encodeURIComponent(contractId)}/photos?type=depart`,
      {},
      credentials
    )
  },

  // ── Récupérer un document MRC par son idDoc ─────────────────────────────────
  // Retourne { Nom, Mime, Data (base64) }
  async getDocument(docId, credentials) {
    return apiCall(
      `/api/documents/${encodeURIComponent(docId)}`,
      {},
      credentials
    )
  },

  // ── Récupérer EDL complet (départ + retour) ─────────────────────────────────
  async getEDL(contractId, credentials) {
    return apiCall(
      `/api/edl/${encodeURIComponent(contractId)}`,
      {},
      credentials
    )
  },

  // ── Envoi email avec PDF joint — via Resend ──────────────────────────────────
// ✅ Migré de /api/send-email (Mailjet) vers /api/send-report (Resend)
// Signature étendue : ajout du 8e paramètre `plate` pour le badge immat
async sendEmail(to, toName, subject, pdfBase64, filename, contractId, credentials, plate) {
  return apiCall(
    '/api/send-report',
    {
      method: 'POST',
      body: JSON.stringify({
        to,
        toName,
        subject,
        pdfBase64,
        filename,
        contractId,
        plate: plate || '',   // ✅ badge immatriculation dans le template HTML
      }),
    },
    credentials,
    TIMEOUT_SAVE_MS
  )
},

  // ── Test agence ──────────────────────────────────────────────────────────────
  async testAgency(codeAgence, credentials) {
    return apiCall(
      '/api/test-agency',
      { method: 'POST', body: JSON.stringify({ codeAgence }) },
      credentials
    )
  },
}

// ── Données mock pour mode démo / tests ───────────────────────────────────────
// ✅ startDate en getter pour toujours retourner la date courante
export const MOCK_CONTRACTS = [
  {
    id: 1, rawId: 1, typeDocument: 'CL',
    plate: 'AB-123-CD', vehicleName: 'Renault Clio', vehicleType: 'VP',
    clientName: 'Jean Dupont', driverName: 'Jean Dupont',
    clientEmail: 'jean@example.com', agencyEmail: '',
    get startDate() { return new Date().toISOString() },
    endDate: null, mileage: 45230, fuel: null, kmInclus: 1000,
  },
  {
    id: 2, rawId: 2, typeDocument: 'CL',
    plate: 'EF-456-GH', vehicleName: 'Peugeot Partner', vehicleType: 'VU',
    clientName: 'Marie Martin', driverName: 'Marie Martin',
    clientEmail: 'marie@example.com', agencyEmail: '',
    get startDate() { return new Date().toISOString() },
    endDate: null, mileage: 82100, fuel: null, kmInclus: 2000,
  },
]